// ============================================================
// bill-fields.js
// ------------------------------------------------------------
// นิยามช่องค่าใช้จ่ายของบิล + ตรรกะ "ตัดรายการที่เก็บเงินไปแล้ว"
// ใช้ร่วมกันในหน้าที่ต้องรวมยอดค้างชำระ (Billing, WaterPaymentInfo, RecordReceipt, ReceiptAdjustment)
//
// เดิมหน้าหนึ่งเขียนเป็นลูป อีกหน้าเขียน if ซ้ำ 22 ก้อน ทำให้แก้ตกหล่นได้ง่าย
// ============================================================

import { roundTo } from "../core/format.js";

// ช่องค่าใช้จ่ายในบิล คู่กับฟิลด์ "เก็บเงินช่องนี้แล้ว" (s_*)
//   k  = ฟิลด์ยอดเงิน
//   sk = ฟิลด์บอกว่าเก็บเงินช่องนี้ไปแล้ว
//   m  = ตัวคูณตอนรวมยอด (ลดหนี้เป็น -1)
export const BILL_FIELDS = [
  { k: "water_bill", sk: "s_water_bill" },
  { k: "waste_cost", sk: "s_waste_cost" },
  { k: "equipment_maintenance_fee", sk: "s_equipment_maintenance_fee" },
  { k: "common_expenses", sk: "s_common_expenses" },
  { k: "cable_cost", sk: "s_cable_cost" },
  { k: "fire_cost", sk: "s_fire_cost" },
  { k: "other_income", sk: "s_other_income" },
  { k: "add_debt", sk: "s_add_debt" },
  { k: "meter_connect_cost", sk: "s_meter_connect_cost" },
  { k: "reduce_debt", sk: "s_reduce_debt", m: -1 },
  { k: "late_payment", sk: "s_late_payment" },
];

// ยอดรวมของบิล 1 ใบจากค่าที่อยู่ในตัว item (ไม่สนใจ s_*)
export const sumBillFields = (item) => BILL_FIELDS.reduce((sum, { k, m = 1 }) => sum + Number(item[k] || 0) * m, 0);

// ยอดของบิล 1 ใบ นับเฉพาะช่องที่ "ยังไม่ได้เก็บเงิน"
export const unpaidAmount = (item) => BILL_FIELDS.reduce((sum, { k, sk, m = 1 }) => (item[sk] ? sum : sum + Number(item[k] || 0) * m), 0);

// ช่องนี้ถูกเก็บเงิน (transaction_payments) หรือถูกตัดหนี้ (writeoff_list) ไปแล้วหรือยัง
//
// NOTE s_* ระดับงวดเป็นสถานะรวมของใบ เชื่อรายช่องไม่ได้ (บางเคสเป็น true ทั้งแถวทั้งที่ยังเก็บไม่ครบ)
// ให้ยึด s_* ในลิสต์เป็นหลักเสมอ — ไม่มีลิสต์ = ยังไม่มีใบเสร็จ/ใบตัดหนี้ที่ยังไม่ถูกยกเลิก จึงถือว่ายังไม่จบทุกช่อง
// (ห้าม fallback ไป item[sk] เพราะหลัง "ยกเลิกการตัดหนี้"/"ยกเลิกใบเสร็จ" backend ไม่ได้ล้าง s_* ระดับงวด
//  ค่าน้ำ/ค่าบริการอุปกรณ์ที่ถูกตัดหนี้ไว้จึงหายจากยอดค้างทั้งที่ยกเลิกไปแล้ว)
// API ที่ใช้ตรรกะนี้คืนเฉพาะงวด status_pay = 1 (ค้างชำระ) อยู่แล้ว บิลที่ตัดหนี้ทั้งใบแบบเก่า (status_pay = 3) ไม่ติดมาด้วย
export const isWaived = (item, sk) =>
  [item.transaction_payments, item.writeoff_list].some((list) => list?.some((p) => p[sk]));

// ตัดรายการที่เก็บเงินไปแล้ว (transaction_payments) หรือถูกตัดหนี้ทิ้ง (writeoff_list) ให้เป็น 0
// แล้ว sync สถานะกลับลง item[sk] เพื่อให้จุดที่อ่าน s_* ต่อ (unpaidAmount, ตาราง, ใบแจ้งหนี้) ใช้ค่าเดียวกัน
export const applyWaived = (item) => {
  BILL_FIELDS.forEach(({ k, sk }) => {
    const waived = isWaived(item, sk);
    item[sk] = waived;
    if (waived) item[k] = 0;
  });

  item.total_amount = sumBillFields(item);
};

// เรียงตามบ้านเลขที่แบบ "อาคาร/ห้อง" (เช่น 12/3 มาก่อน 12/10)
export const addressSorter = () => (a, b) => {
  const [buildingA, roomA] = a.address_number.split("/").map(Number);
  const [buildingB, roomB] = b.address_number.split("/").map(Number);
  return buildingA !== buildingB ? buildingA - buildingB : roomA - roomB;
};

// ============================================================
// ส่วนที่หน้ารับชำระ/ปรับปรุงใบเสร็จใช้ร่วมกัน
// เดิมทั้ง 2 หน้าประกาศชุดนี้ซ้ำกันคนละก๊อป แก้ที่เดียวไม่ครบ
// ============================================================

// 11 ช่องค่าใช้จ่ายที่แสดงในตาราง "รายการที่ต้องชำระ" (ลำดับตามที่แสดงบนหน้าจอ)
export const PAYMENT_TITLES = {
  water_bill: "ค่าใช้น้ำประจำเดือน",
  equipment_maintenance_fee: "ค่ารักษาอุปกรณ์การใช้น้ำ",
  common_expenses: "ค่าใช้จ่ายส่วนกลาง",
  fire_cost: "ค่าเบี้ยประกัน",
  waste_cost: "ค่าบริการจัดเก็บขยะ",
  cable_cost: "ค่าบริการเคเบิล",
  other_income: "ค่าใช้จ่ายอื่นๆ",
  add_debt: "รายการเพิ่มหนี้",
  reduce_debt: "รายการลดหนี้",
  meter_connect_cost: "ค่าต่อกลับมิเตอร์",
  late_payment: "ค่าปรับชำระล่าช้า",
};

export const PAYMENT_KEYS = Object.keys(PAYMENT_TITLES);

// ตัวคูณตอนรวมยอด — ลดหนี้ต้องหักออก
export const signOf = (key) => (key === "reduce_debt" ? -1 : 1);

// ★ เงื่อนไขเดียวที่ใช้ตอบว่า "ช่องนี้ของงวดนี้จบแล้วหรือยัง"
// จบเมื่อ ถูกเก็บเงิน/ตัดหนี้ไปแล้ว (isWaived) หรือ payment_list ของงวดนั้นระบุว่าชำระแล้ว
// ห้าม fallback ไป row[`s_${key}`] — ดู NOTE ของ isWaived ด้านบน
export const isDone = (row, key) => isWaived(row, `s_${key}`) || !!row.payment_list?.find((e) => e.title === key && e.status_pay);

// ยอดค้างจริงของบิล 1 งวด (นับเฉพาะช่องที่ยังไม่จบ)
export const unpaidAmountOf = (row) => PAYMENT_KEYS.reduce((sum, key) => sum + (isDone(row, key) ? 0 : Number(row[key] || 0) * signOf(key)), 0);

// เงินสดปัดเศษเป็นขั้นละ 25 สตางค์
export const SATANG_STEP = 0.25;

// เงินสดต้องปัดยอดขึ้นให้ลงตัวขั้นละ 25 สตางค์ — คืนส่วนต่างที่ต้องปรับเพิ่ม
export const calcChangeSatang = (amount) => {
  const remainder = amount % SATANG_STEP;
  if (remainder === 0) return 0;
  return roundTo(SATANG_STEP - remainder, 2);
};
